import SbButton from '@Components/Button';
import { AppDispatch, RootStateType } from '@Store';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Image from '../../../../components/Image';
import SbTooltip from '../../../../components/Tooltip';
import { CSPChargePropsType } from '../../../../store/reducers/GetRates/Type';
import { BackToLoginModal } from '../BackToLoginModal';
import ChargesBreakup from '../ChargesBreakup';
import { BookBtn, CSPLogo, SbChip, TableRow } from '../styles';
import { setBookNowAuth } from '@Reducers/GetRates';
import { useDispatch } from 'react-redux';
import { setFinalChargeableWeight } from '@Reducers/Shipment';
import useAccountLookup from '@Hook/useAccountLookup';
import { Tooltip } from '@mui/material';
import { createShipmentContextType } from '../../../../types/shipment';
import CreateShipmentContext from '../../../../context/CreateShipment';

type Warehouse = {
  _id: string;
  _systemId: string[];
};

function getWarehouseIdBySystemId(
  systemId: string | undefined,
  warehouseListForShipment?: { list?: Warehouse[] },
): string | undefined {
  if (!systemId) return undefined; // Guard clause

  return warehouseListForShipment?.list?.find(warehouse => warehouse._systemId.includes(systemId))?._id;
}

interface HurricaneData {
  currency?: string | null;
  duty?: {
    amount?: number;
    currency?: string;
  };
  tax?: {
    amount?: number;
    currency?: string;
  };
  products?: Array<{
    description: string;
    dutyRate: number;
    taxRate: number;
  }>;
}

interface SingleCSPProps extends CSPChargePropsType {
  hurricaneData?: HurricaneData;
  dutyChargeAmount?: number;
}

function SingleCSP(props: SingleCSPProps) {
  const { CSPCharge, handleBookCSP, renderedFrom, isSelected = false, hurricaneData, dutyChargeAmount } = props;

  const getDutyTaxDisplay = (data: HurricaneData | undefined): string | null => {
    if (!data) return null;
    const hasDuty = data.duty && typeof data.duty.amount === 'number';
    const hasTax = data.tax && typeof data.tax.amount === 'number';
    if (!hasDuty && !hasTax) return null;

    const dutyAmount = data.duty && typeof data.duty.amount === 'number' ? data.duty.amount : 0;
    const taxAmount = data.tax && typeof data.tax.amount === 'number' ? data.tax.amount : 0;
    const total = dutyAmount + taxAmount;
    const currencyVal = data.duty?.currency || data.tax?.currency || data.currency || 'USD';
    return `${total.toFixed(2)} ${currencyVal}`;
  };

  const displayDutyTax = getDutyTaxDisplay(hurricaneData);
  const [openBackToLoginModal, setBackToLoginModal] = useState(false);
  const {
    transitTime,
    serviceName,
    serviceImage,
    totalPricing,
    fastest,
    cheapest,
    pricing,
    taxPricing,
    displayChargeableWeight,
  } = CSPCharge;
  const modalId = 'GetRatesV2Main';
  const { selectedUser } = useAccountLookup(modalId);
  const shipmentContext = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const pickupType = shipmentContext?.pickupType;
  const setPickupType = shipmentContext?.setPickupType;
  const packageData = shipmentContext?.packageData;
  const setPackageData = shipmentContext?.setPackageData;

  const [currency, setCurrency] = useState<{ name?: string; symbol?: string; fullForm?: string }>({});
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const dispatch = useDispatch<AppDispatch>();
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const { warehouseListForShipmentOptionsHashById } = useSelector((state: RootStateType) => state.Warehouse);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);

  useEffect(() => {
    const stored = localStorage.getItem('currency');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCurrency(parsed);
      } catch (e) {
        console.error('Failed to parse stored currency:', e);
      }
    }
  }, []);

  const bookCSP = (CSPCharge: CSPChargePropsType['CSPCharge'] | null) => {
    const warehouseId = getWarehouseIdBySystemId(CSPCharge?._systemId, warehouseListForShipent);
    if (
      warehouseId &&
      name === 'Cargo Solutions Network' &&
      setPackageData &&
      setPickupType &&
      packageData &&
      pickupType
    ) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[warehouseId];
      const coAttention = selectedWarehouse.coAttention;
      const [telephoneCode, number] = coAttention.contact;
      const countryId = selectedWarehouse._addressId.country;
      const warehouseAddress = {
        ...selectedWarehouse._addressId,
        country: countryHashById?.[countryId]?.name,
        name: selectedWarehouse.name,
        _countryId: countryHashById?.[countryId]?._id,
        phone: { telephoneCode, number, name: coAttention.name },
        email: coAttention.email,
      };
      setPackageData({
        ...packageData,
        isDropOff: true,
        ...(CSPCharge?.currency && {
          currency: CSPCharge.currency || defaultOrgCountryCurrency,
        }),
      });
      setPickupType({ ...pickupType, selectedWearhouseAddress: warehouseAddress, warehouse: warehouseId });
    }
    if (token?._id) {
      handleBookCSP(CSPCharge);
      dispatch(setBookNowAuth(true));
    } else {
      setBackToLoginModal(true);
      dispatch(setBookNowAuth(false));
    }
  };

  const handleCloseBackToLoginModal = () => {
    setBackToLoginModal(false);
  };

  const isAccountSuspended = selectedUser?.onBoardedStatus ? selectedUser?.onBoardedStatus === 'suspended' : false;
  const { warehouseListForShipent } = useSelector((state: RootStateType) => state.Warehouse);

  return (
    <>
      <TableRow
        item
        xs={12}
        compactview={renderedFrom === 'admin-shipment-history' ? '1' : '0'}
        sx={{
          display: 'grid',
          gridTemplateColumns: displayDutyTax ? '83px 1fr 110px 150px 130px 130px' : '83px 1fr 110px 150px 130px',
          alignItems: 'center',
          columnGap: '16px',
          boxSizing: 'border-box',
          padding: '0 24px',
          width: '100%',
        }}
      >
        {/* Col 1 – Logo */}
        <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CSPLogo>
            <img
              src={serviceImage}
              style={{
                width: '70%',
                height: '70%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </CSPLogo>
        </Grid>

        {/* Col 2 – Carrier name + badges */}
        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Grid
            item
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Typography
              className="md-text font-semibold"
              sx={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.3,
              }}
            >
              {name === 'Cargo Solutions Network' ? serviceName : transitTime + ' Days'}
            </Typography>

            <Typography
              className="sm-text"
              sx={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.3,
              }}
            >
              {name === 'Cargo Solutions Network' ? transitTime + ' Days' : serviceName}
            </Typography>
          </Grid>
          {(fastest || cheapest) && (
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}
            >
              {fastest && (
                <SbChip
                  className="font-semibold"
                  sx={{
                    color: '#127E44',
                    background: '#ADFFB5',
                    '& .MuiChip-icon': { color: '#127E44' },
                    marginLeft: 0,
                  }}
                  icon={<BoltIcon />}
                  label="Fastest"
                />
              )}
              {cheapest && (
                <SbChip
                  className="font-semibold"
                  sx={{
                    color: '#12647E',
                    background: '#ADE6FF',
                    '& .MuiChip-icon': { color: '#12647E' },
                    marginLeft: 0,
                  }}
                  icon={<AccountBalanceWalletIcon />}
                  label="Cheapest"
                />
              )}
            </Grid>
          )}
        </Grid>

        {/* Col 3 – Chargeable Weight */}
        <Grid item sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
          <Typography className="font-semibold">{`${displayChargeableWeight?.toFixed(2)} kg`}</Typography>
        </Grid>

        {/* Col 4 – Cost / Chargeable Rate */}
        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography className="md-text font-semibold" sx={{ whiteSpace: 'nowrap' }}>
            {totalPricing
              ? `${CSPCharge?.currency || defaultOrgCountryCurrency || currency?.name || ''} ${parseFloat(
                  totalPricing?.toString(),
                ).toFixed(2)}`
              : ''}
          </Typography>
          <SbTooltip
            title={
              <ChargesBreakup
                pricing={pricing}
                totalPricing={totalPricing}
                taxPricing={taxPricing}
                currency={CSPCharge?.currency || defaultOrgCountryCurrency || currency?.name || ''}
                dutyChargeAmount={dutyChargeAmount}
              />
            }
            placement={'left'}
          >
            <Grid
              container
              item
              alignItems={'center'}
              justifyContent={'center'}
              style={{ cursor: 'default', width: 'fit-content' }}
            >
              <Image src={'/images/Boxes/Help.svg'} alt="help" style={{ height: '16px', width: '16px' }} />
              <Typography style={{ textDecoration: 'underline' }} className="ml-1/2">
                View Breakup
              </Typography>
            </Grid>
          </SbTooltip>
        </Grid>

        {/* Col 5 – Duty + Tax (conditional) */}
        {displayDutyTax && (
          <Grid item sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
            <Typography className="md-text font-semibold">{displayDutyTax}</Typography>
          </Grid>
        )}

        {/* Col 5/6 – Action */}
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {isSelected ? (
            <SbButton variant="contained" onClick={() => bookCSP(null)}>
              Selected
            </SbButton>
          ) : (
            <Tooltip
              title={isAccountSuspended ? 'This account is suspended' : ''}
              disableHoverListener={!isAccountSuspended}
            >
              <span>
                <BookBtn
                  variant="outlined"
                  className="font-semibold"
                  disabled={isAccountSuspended}
                  onClick={() => {
                    const res = displayChargeableWeight?.toFixed(2);
                    dispatch(setFinalChargeableWeight(parseFloat(res)));
                    bookCSP(CSPCharge);
                  }}
                  style={{ zIndex: '2', whiteSpace: 'nowrap' }}
                >
                  Book Now
                </BookBtn>
              </span>
            </Tooltip>
          )}
        </Grid>
      </TableRow>
      <BackToLoginModal open={openBackToLoginModal} onClose={handleCloseBackToLoginModal} />
    </>
  );
}

export default SingleCSP;
